<div class="item-write-overlay" *ngIf="fg">
  <div class="item-write-dialog" [class.__seller]="!isPricingRole" (click)="onDialogClick($event)">
    <form [formGroup]="fg">
      <div class="item-wrapper item-detailed-modal item-write" [class.__readonly]="readOnlyNotice || archivedLock">
        <div *ngIf="loader" class="loader"></div>

        <div class="item-header">
          <span class="item-header__title">{{ modalTitle }}</span>
          <div class="close" role="button" (click)="close()"></div>
        </div>

        <!-- Clone provenance (HUBX-8422): the item this form was seeded FROM.
             Rendered only for a clone — a plain Add Item has no source and the
             whole band collapses. The band is a row so the target-company
             selector (sibling sub-task) lands on its right without reshaping
             this card; all three drafts read FROM -> TO. -->
        <div class="clone-band" *ngIf="showBand">
          <!-- Edit header (HUBX-8388): promo badges + the two activity timestamps, from
               the grid row. Reuses the clone card's geometry — same white surface, same
               one-baseline facts row — because it does the same job on the other side of
               the same band: read-only context above a form.

               No section title, unlike the clone card. That one needs "Cloning From"
               because its whole reason to exist IS the provenance claim: without it, the
               facts belong to no stated item. Here every fact labels itself, and the modal
               header already says Edit Item — a second "This Item" would say it twice. -->
          <div class="clone-source __untitled" *ngIf="header">
            <div class="clone-source__facts">
              <div class="clone-source__fact" *ngIf="header.launchedOn">
                <span class="clone-source__fact-label">Launched</span>
                <span class="clone-source__fact-value">{{ headerLaunchedOn | date:'M/d/yy, h:mm a' }}</span>
              </div>
              <div class="clone-source__fact" *ngIf="header.updatedOn">
                <span class="clone-source__fact-label">Updated</span>
                <span class="clone-source__fact-value">{{ headerUpdatedOn | date:'M/d/yy, h:mm a' }}</span>
              </div>
            </div>
          </div>

          <div class="clone-source" *ngIf="source">
            <div class="clone-source__head">
              <svg class="clone-source__glyph" width="14" height="14" viewBox="0 0 24 24" fill="none"
                xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <rect x="8.5" y="8.5" width="11" height="11" rx="2" stroke="currentColor" stroke-width="2"/>
                <path d="M15.5 5.5H6.5C5.39543 5.5 4.5 6.39543 4.5 7.5V16.5" stroke="currentColor" stroke-width="2"
                  stroke-linecap="round"/>
              </svg>
              <span>Cloning From</span>
            </div>

            <!-- Four facts on one baseline. Each cell is guarded on its own, and
                 an absent fact drops the whole cell rather than leaving a label
                 over a blank: a Seller gets no company, and an item that never
                 went live has no launch date. -->
            <div class="clone-source__facts">
              <div class="clone-source__fact">
                <span class="clone-source__fact-label">Part Number</span>
                <span class="clone-source__fact-value __ident">{{ source.vendorItemCode }}</span>
              </div>
              <div class="clone-source__fact" *ngIf="source.companyName">
                <span class="clone-source__fact-label">Company</span>
                <span class="clone-source__fact-value __company">{{ source.companyName }}</span>
              </div>
              <div class="clone-source__fact" *ngIf="source.launchedOn">
                <span class="clone-source__fact-label">Launched</span>
                <span class="clone-source__fact-value">{{ launchedOnLocal | date:'M/d/yy, h:mm a' }}</span>
              </div>
              <div class="clone-source__fact" *ngIf="source.updatedOn">
                <span class="clone-source__fact-label">Updated</span>
                <span class="clone-source__fact-value">{{ updatedOnLocal | date:'M/d/yy, h:mm a' }}</span>
              </div>
            </div>
          </div>

          <!-- FROM -> TO. The arrow is meaningless with only one half present, so it
               renders only when both are. -->
          <div class="clone-band__arrow" *ngIf="source && showTargetSelector" aria-hidden="true">→</div>

          <!-- Target company (ADR-0010, HUBX-8421). Operator roles only. Preselected to
               the company already being acted as, so leaving it alone reproduces the
               request exactly as it was before this control existed; picking another is a
               deliberate cross-company create. Read-only use of the impersonation list —
               choosing here never switches the session's company. -->
          <div class="clone-target" *ngIf="showTargetSelector">
            <div class="clone-target__label">Target Company</div>
            <!-- Purpose-built here rather than reusing the shared app-dropdown: that
                 component only offers its filter box together with `.customer-select`,
                 the 72px black company switcher from the top bar, so search and the
                 top-bar skin cannot be separated. This keeps the closed control
                 identical to the other selects in this popup — same class, same chevron
                 — and adds only what they lack: a company glyph and a search box. -->
            <div class="clone-target__control">
              <button type="button" class="status-select clone-target__toggle"
                [class.__open]="companyPickerOpen" [disabled]="!companies.length"
                (click)="toggleCompanyPicker()">
                <span class="clone-target__value" [class.__placeholder]="!selectedCompanyName">
                  {{ selectedCompanyName || 'Select a company' }}
                </span>
              </button>

              <div class="clone-target__panel" *ngIf="companyPickerOpen">
                <input #companySearch type="text" class="clone-target__search" autocomplete="off"
                  placeholder="Search companies" [(ngModel)]="companyFilter"
                  [ngModelOptions]="{ standalone: true }" (click)="stopPropagation($event)">
                <div class="clone-target__options">
                  <button type="button" class="clone-target__option"
                    *ngFor="let company of filteredCompanies"
                    [class.__selected]="company.vendorId === fg.controls.targetVendorId.value"
                    (click)="selectCompany(company)">
                    {{ company.companyName }}
                  </button>
                  <div class="clone-target__empty" *ngIf="!filteredCompanies.length">No matches</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="item-top-info">

          <!-- LEFT: identity + description on top, taxonomy row (XID / category / manufacturer) below -->
          <div class="item-top-left">

            <div class="item-identity">
              <!-- part number / MPN stacked -->
              <div class="item-id-col">
                <div class="item-info-title">Part Number<span class="req">*</span></div>
                <input type="text" class="form-control" formControlName="vendorItemCode"
                  [class.__invalid]="showFieldError('vendorItemCode')">
                <div class="item-info-title">MPN<span class="req">*</span></div>
                <input type="text" class="form-control" formControlName="mpn"
                  [class.__invalid]="showFieldError('mpn')"
                  [class.__conflict]="conflictOpen('mpn')">
              </div>

              <!-- description -->
              <div class="item-description">
                <!-- the title line carries Description's counter, on focus and while over the limit -->
                <div class="item-info-title __with-counter"><span>Description<span class="req">*</span></span> <app-char-counter *ngIf="showDescriptionCounter" [length]="descriptionLength" [max]="descriptionMax"></app-char-counter></div>
                <textarea class="form-control" formControlName="description" [class.__invalid]="showFieldError('description')" [class.__conflict]="conflictOpen('description')"
                  (focus)="descriptionFocused = true" (blur)="descriptionFocused = false"></textarea>
              </div>
            </div>

            <!-- taxonomy: XID cell reserved (content ADMIN/VM only) so Manufacturer aligns
                 under Description for every role; the group below spans Description's width -->
            <div class="item-taxonomy">
              <div class="item-labeled-control item-xid">
                <ng-container *ngIf="isPricingRole">
                  <div class="item-info-title">XID</div>
                  <input type="text" class="form-control" formControlName="hubxItemCode"
                    [class.__invalid]="showFieldError('hubxItemCode')"
                    [class.__conflict]="conflictOpen('hubxItemCode')">
                </ng-container>
              </div>

              <div class="item-taxonomy-group">
                <div class="item-labeled-control">
                  <div class="item-info-title">Manufacturer<span class="req">*</span></div>
                  <select class="status-select status-select--manufacturer" formControlName="manufacturerId"
                    [class.__invalid]="showFieldError('manufacturerId')"
                    [class.__conflict]="conflictOpen('manufacturerId')">
                    <option value="" disabled hidden>Select</option>
                    <option *ngFor="let man of manufacturers" [value]="man.id">{{ man.name }}</option>
                  </select>
                </div>

                <div class="item-labeled-control">
                  <div class="item-info-title">Category<span class="req">*</span></div>
                  <select class="status-select status-select--main" formControlName="categoryMainId" (change)="onMainCategoryChange()"
                    [class.__invalid]="showCategoryError()"
                    [class.__conflict]="conflictOpen('categoryId')">
                    <option value="" disabled hidden>Select</option>
                    <option *ngFor="let cat of categories" [value]="cat.categoryId">{{ cat.name }}</option>
                  </select>
                  <!-- The ring goes on the MAIN select as well as the leaf: the leaf is not
                       rendered at all when the category has no children, so a conflict marked
                       only there would be invisible on exactly those items. -->
                </div>

                <div class="item-labeled-control" *ngIf="subCategories.length">
                  <div class="item-info-title">Subcategory<span class="req">*</span></div>
                  <select class="status-select" formControlName="categoryId" (change)="onSubCategoryChange()"
                    [class.__invalid]="showCategoryError()"
                    [class.__conflict]="conflictOpen('categoryId')">
                    <option value="" disabled hidden>Select</option>
                    <option *ngFor="let sub of subCategories" [value]="sub.categoryId">{{ sub.name }}</option>
                  </select>
                </div>
              </div>
            </div>

            <!-- Pre-submit category nudge (HUBX-8431). Advisory only: this MPN already
                 matches an item filed elsewhere, which a per-vendor category is allowed
                 to differ from (ADR-0007). It never rings a field and never blocks
                 submit — hence its own quiet amber line under the category selects,
                 deliberately NOT the red validation banner in the footer. -->
            <div class="item-category-nudge" *ngIf="categoryNudge">
              <span>{{ categoryNudge }}</span>
            </div>

          </div>

          <!-- RIGHT: quantities + pricing + DQ -->
          <div class="item-top-right">

            <!-- available / ETA -->
            <div class="item-availability">
              <div class="item-info-title">Available<span class="req">*</span></div>
              <input type="number" class="form-control" formControlName="availability"
                [class.__invalid]="showFieldError('availability')"
                [class.__conflict]="conflictOpen('availability')">
              <div class="item-info-title">ETA<span class="req">*</span></div>
              <input type="number" class="form-control" formControlName="leadTimeDays"
                [class.__invalid]="showFieldError('leadTimeDays')"
                [class.__conflict]="conflictOpen('leadTimeDays')">
            </div>

            <!-- MOQ / MXQ — all roles. MOQ is optional (server defaults to 1). -->
            <div class="item-moq-group">
              <div class="item-info-title" [tooltip]="'Minimum Order Quantity'" placement="top">MOQ</div>
              <div class="input-qty">
                <input type="number" class="form-control" formControlName="moq" placeholder="1"
                  [class.__invalid]="showFieldError('moq')"
                  [class.__conflict]="conflictOpen('moq')">
              </div>
              <div class="item-info-title" [tooltip]="'Maximum Order Quantity'" placement="top">MXQ</div>
              <div class="input-qty">
                <input type="number" class="form-control" formControlName="mxq"
                  [class.__invalid]="showFieldError('mxq') || showGroupError('moqExceedsMxq', 'moq', 'mxq')"
                  [class.__conflict]="conflictOpen('mxq')">
              </div>
            </div>

            <!-- cost / price stacked (Price ADMIN/VM only) -->
            <div class="item-cost-price">
              <div class="item-info-title">Cost<span class="req">*</span></div>
              <div class="input-price">
                <input type="text" class="form-control" formControlName="cost" prefix="$" mask="separator.2" thousandSeparator=","
                  [class.__invalid]="showFieldError('cost')"
                  [class.__conflict]="conflictOpen('cost')">
              </div>
              <ng-container *ngIf="isPricingRole">
                <div class="item-info-title">Price<span class="req">*</span></div>
                <div class="input-price">
                  <input type="text" class="form-control" formControlName="unitPrice" prefix="$" mask="separator.2" thousandSeparator=","
                    [class.__invalid]="showFieldError('unitPrice') || showGroupError('unitPriceBelowCost', 'unitPrice', 'cost')"
                    [class.__conflict]="conflictOpen('unitPrice')">
                </div>
              </ng-container>
            </div>

            <!-- DQ #1-3 (ADMIN/VM only); the group carries the gap that separates it from Price.
                 The conflict ring goes on the GROUP, not on a tier: the tiers are a single
                 member of the body, so the store speaks about them as a collection and
                 ringing one of the three would name a granularity the PUT does not have. -->
            <div class="item-dq-group" *ngIf="isPricingRole"
              [class.__conflict]="conflictOpen('discountQtys')">
              <div class="item-dq" *ngFor="let dq of dqControls.controls; let i = index" formArrayName="discountQtys">
                <div class="item-info-title" [tooltip]="'Discount Quantity'" placement="top">DQ #{{ i + 1 }}</div>
                <div class="input-group-vertical" [formGroupName]="i">
                  <!-- The rows' masks (HUBX-8466): '<' on the quantity, '$' and thousands on the price. -->
                  <div class="input-qty">
                    <input type="text" class="form-control" formControlName="fromQty" prefix="<" mask="0*"
                      #qtyInput (blur)="checkPrefix(qtyInput)"
                      [class.__invalid]="dqInvalid(i, 'fromQty')">
                  </div>
                  <div class="input-price disc">
                    <input type="text" class="form-control" formControlName="price" prefix="$" mask="separator.2" thousandSeparator=","
                      #tierPrice (blur)="checkPrefix(tierPrice)"
                      [class.__invalid]="dqInvalid(i, 'price')">
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        <div class="item-bottom-info">
          <!-- attributes + Restrictions/Special Notes chips, single grey band (EXW left of Restrictions) -->
          <div class="item-attributes">

            <div class="item-labeled-control" *ngIf="condition">
              <div class="item-info-title">Condition<span class="req">*</span></div>
              <select class="item-bottom-select input-group-condition" formControlName="condition"
                [class.__invalid]="showFieldError('condition')"
                [class.__conflict]="conflictOpen('condition')">
                <option value="" disabled hidden>Select</option>
                <option *ngFor="let cond of condition.values" [value]="cond.code">{{ cond.value }}</option>
              </select>
            </div>

            <div class="item-labeled-control" *ngIf="warranty">
              <div class="item-info-title">Warranty<span class="req">*</span></div>
              <select class="item-bottom-select input-group-warranty" formControlName="warranty"
                [class.__invalid]="showFieldError('warranty')"
                [class.__conflict]="conflictOpen('warranty')">
                <option value="" disabled hidden>Select</option>
                <option *ngFor="let wart of warranty.values" [value]="wart.code">{{ wart.value }}</option>
              </select>
            </div>

            <div class="item-labeled-control" *ngIf="packaging">
              <div class="item-info-title">Packaging<span class="req">*</span></div>
              <select class="item-bottom-select input-group-packaging" formControlName="packaging"
                [class.__invalid]="showFieldError('packaging')"
                [class.__conflict]="conflictOpen('packaging')">
                <option value="" disabled hidden>Select</option>
                <option *ngFor="let pckg of packaging.values" [value]="pckg.code">{{ pckg.value }}</option>
              </select>
            </div>

            <div class="item-labeled-control">
              <div class="item-info-title">EXW<span class="req">*</span></div>
              <select class="item-bottom-select input-group-exw" formControlName="exwId"
                [class.__invalid]="showFieldError('exwId')"
                [class.__conflict]="conflictOpen('exwId')">
                <option value="" disabled hidden>Select</option>
                <option *ngFor="let exw of exwPoints" [value]="exw.id">{{ exw.description }}</option>
              </select>
            </div>

            <!-- Restrictions chip: icon + None/… + Add|Edit/View -->
            <div class="item-labeled-control item-note-control">
              <div class="item-info-title">Restrictions</div>
              <div class="item-note-chip" role="button" (click)="openTextModal('restrictions')"
                [class.has-text]="hasText('restrictions')" [class.__disabled]="chipDisabled('restrictions')"
                [class.__conflict]="conflictOpen('restrictions')"
                [tooltip]="hasText('restrictions') ? fg.controls.restrictions.value : ''" placement="top">
                <i class="item-note-chip__icon __restrictions"></i>
                <span class="item-note-chip__state">{{ hasText('restrictions') ? '…' : 'None' }}</span>
                <span class="item-note-chip__action" *ngIf="chipAction('restrictions')">{{ chipAction('restrictions') }}</span>
              </div>
            </div>

            <!-- Special Notes chip -->
            <div class="item-labeled-control item-note-control">
              <div class="item-info-title">Special Notes</div>
              <div class="item-note-chip" role="button" (click)="openTextModal('specialNotes')"
                [class.has-text]="hasText('specialNotes')" [class.__disabled]="chipDisabled('specialNotes')"
                [class.__conflict]="conflictOpen('specialNotes')"
                [tooltip]="hasText('specialNotes') ? fg.controls.specialNotes.value : ''" placement="top">
                <i class="item-note-chip__icon __notes"></i>
                <span class="item-note-chip__state">{{ hasText('specialNotes') ? '…' : 'None' }}</span>
                <span class="item-note-chip__action" *ngIf="chipAction('specialNotes')">{{ chipAction('specialNotes') }}</span>
              </div>
            </div>

          </div>
        </div>

        <div class="item-write-footer">
          <!-- status → CreateApproved flag only (ADR-0009 rule 5); parked here so it doesn't claim a top-row column.
               SELLER: locked to Pending (lock glyph + long hint on hover); ADMIN/VM: Pending/Active selectable.
               Create only: the PUT has no createApproved member; on Edit the collapsed Status
               control (skuStatus) travels as the `status` intent instead. Showing this on Edit
               would offer a control that changes nothing. -->
          <div class="item-status" *ngIf="!isEdit">
            <div class="item-info-title">Status<span class="req">*</span></div>
            <div class="item-status-row">
              <select class="status-select" formControlName="status"
                [tooltip]="isPricingRole ? '' : sellerStatusHint" placement="top">
                <option value="Pending">Pending</option>
                <option value="Active" *ngIf="isPricingRole">Active</option>
              </select>
              <div class="item-status-hint">
                <span class="item-status-hint__dot" [class.__active]="fg.controls.status.value === 'Active'"></span>
                <span>{{ statusHint }}</span>
              </div>
            </div>
          </div>

          <!-- Status (Edit only). Bottom-left, the corner the Create Status block holds —
               status is context, not an action, so it belongs where the eye starts rather
               than in the action group.

               ONE select, collapsed, matching the legacy popup the vendors already use.
               The domain keeps three separate axes (ADR-0009) and this control does not
               contradict that: it shows the projection of all three plus IsArchived and
               inventory (EffectiveStatus, ADR-0009 Rule 7) and writes back the chosen value
               as the `status` intent — the server decides which axis it moves (Rule 9).

               Three values are assignable and lead the list under "You can set". Active
               and Paused name a vendor intent; Sold Out is a shortcut that zeroes
               Available (see the component) rather than moving any axis.

               The other four sit under "Automatic", disabled:
                 - Inactive / Pending: computed from the other axes, so picking them
                   could not mean anything.
                 - Removed: assigned by the sales job alone, for every role (ADR-0009
                   Rule 6). Legacy lets an ADMIN pick it; that is a deliberate departure.
                 - Archived: the one entry the heading fits loosely, since the Archive
                   button in this same footer sets it. It sits here because what the two
                   groups really separate is what this CONTROL can set, and the button is
                   not this control.

               The grouping is what makes that legible. A disabled <option> is greyed and
               nothing else, and the exact grey varies by browser and OS — too weak a
               signal for four of seven entries, and it says "no" without saying why. The
               headings say why, and cost the legacy ORDER.

               The hint beside the select covers what the grouping cannot: the list is
               only visible while it is open, and the state a user needs to understand is
               the one showing when it is closed. -->
          <div class="item-status __collapsed" *ngIf="isEdit">
            <div class="item-info-title">Status</div>
            <div class="item-status-row">
              <select class="status-select" formControlName="skuStatus"
                [class.__conflict]="conflictOpen('skuStatus')">
                <optgroup label="You can set">
                  <option *ngFor="let option of assignableStatusOptions" [value]="option.value">
                    {{ option.label }}
                  </option>
                </optgroup>
                <optgroup label="Automatic">
                  <option *ngFor="let option of automaticStatusOptions" [value]="option.value" disabled>
                    {{ option.label }}
                  </option>
                </optgroup>
              </select>
              <div class="item-status-hint" *ngIf="skuStatusHint">
                <span class="item-status-hint__dot" [ngClass]="skuStatusTone"></span>
                <span>{{ skuStatusHint }}</span>
              </div>
            </div>
          </div>

          <!-- The three promotions, one row, centred in the space between Status and the
               action group (2026-08-24). They were split before: two badges inside the
               header card and Today's Deals as a switch here, on the reasoning that a
               read-only fact and a write member are different kinds of thing. Too literal
               a reading — to a vendor these are one row of promotions, which is how the
               legacy grid has always shown them, and separating them made the set
               unreadable as a set.

               The footer rather than the header band, because ONE of the three is a
               control and the band is read-only context: a button inside it would break
               that reading, and splitting the row to respect it is what failed before.

               Legacy's shell and legacy's glyphs: a white button-like plate per
               promotion with the active/inactive asset pair inside. All three always
               render, and the plate stays lit while only the glyph fades — an OFF badge
               reads as a button that is off, where a bare dimmed glyph read as an icon
               that failed to load.

               Today's Deals leads the row: it is the one thing here anybody can act on,
               so it takes the position the eye reaches first. It also says so at rest,
               wearing the house blue on its border against its neighbours' neutral
               hairline, and inverts on hover — dark plate, light glyph. A caller without
               `mayCurate` falls back to the neutral border and does not invert, because
               either would promise a click the server refuses. -->
          <div class="item-promos" *ngIf="isEdit">
            <div class="item-promos__label">Promotions</div>
            <div class="item-promos__row">
              <!-- A real <button>, so `disabled` refuses the click natively and is
                   announced; role=switch + aria-checked because a two-state button is not
                   one by default. -->
              <button type="button" class="item-promos__badge __action" role="switch"
                [attr.aria-checked]="inTodaysDeals" [class.__on]="inTodaysDeals"
                [class.__conflict]="conflictOpen('todaysDeals')"
                [disabled]="!mayCurate || archivedLock" (click)="toggleTodaysDeals()"
                [tooltip]="mayCurate
                  ? (inTodaysDeals ? 'Today\'s Deals — click to remove' : 'Today\'s Deals — click to add')
                  : 'Today\'s Deals — only HUBX can change this'" placement="top">
                <img [src]="inTodaysDeals
                  ? 'assets/images/vendor-icons/today-deal-active.svg'
                  : 'assets/images/vendor-icons/today-deal-inactive.svg'" alt="Today's Deals">
              </button>

              <span class="item-promos__badge" [class.__on]="header?.priceDrop"
                tooltip="Price Drop" placement="top">
                <img [src]="header?.priceDrop
                  ? 'assets/images/vendor-icons/price-drop-active.svg'
                  : 'assets/images/vendor-icons/price-drop-inactive.svg'" alt="Price Drop">
              </span>

              <span class="item-promos__badge" [class.__on]="header?.justLaunched"
                tooltip="Just Launched" placement="top">
                <img [src]="header?.justLaunched
                  ? 'assets/images/vendor-icons/just-launched-active.svg'
                  : 'assets/images/vendor-icons/just-launched-inactive.svg'" alt="Just Launched">
              </span>
            </div>
          </div>

          <div class="line-error" *ngIf="lookupsError">
            <span>Could not load form data. Close and try again.</span>
          </div>

          <!-- Archive / Restore (HUBX-8438). Sits with Undo and Save rather than across
               the footer: the three are what this modal DOES, and the bottom-left corner
               belongs to the status axes. Kept a little apart from Undo all the same —
               it takes the listing off the shelf, and flush against the safe actions is
               how it gets hit by accident.

               Icon AND text, and deliberately not a red X: the header already carries an
               X to close, and confusing the two means archiving when you meant to leave.

               Writes nothing on click: it flips the pending intent, Save carries it, Undo
               takes it back. `mayArchive` is server-stated (writableFields), not derived
               from the role. The wrapper div stays: the SCSS gives it the column that
               keeps the button off the footer's shared baseline. -->

          <div class="item-write-footer__archive-control" *ngIf="isEdit && mayArchive">
            <button type="button" class="item-write-footer__archive"
              [class.__restore]="isArchived" (click)="toggleArchived()">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="4" width="18" height="4" rx="1" stroke="currentColor" stroke-width="2"/>
                <path d="M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8" stroke="currentColor" stroke-width="2"/>
                <path *ngIf="!isArchived" d="M12 11v5m0 0-2.5-2.5M12 16l2.5-2.5" stroke="currentColor"
                  stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path *ngIf="isArchived" d="M12 17v-5m0 0-2.5 2.5M12 12l2.5 2.5" stroke="currentColor"
                  stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span>{{ isArchived ? 'Restore' : 'Archive' }}</span>
            </button>
          </div>

          <!-- Undo (Edit only): a local reset to what the GET returned, so it is offered
               only when something has actually moved — a form edit or a pending archive
               intent, which the form itself does not track. -->
          <button type="button" class="item-write-footer__undo" *ngIf="isEdit"
            [disabled]="!canUndo || loader" (click)="undo()">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg">
              <path d="M4 9h11a5 5 0 0 1 0 10h-6" stroke="currentColor" stroke-width="2"
                stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M7.5 5.5 4 9l3.5 3.5" stroke="currentColor" stroke-width="2"
                stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span>Undo</span>
          </button>

          <!-- Cancel is Create-only. On Edit it would be a third way to leave a modal
               that already has two: the header X closes, and Undo puts the form back
               without leaving. Cancel duplicates the X exactly — same outcome, same
               nothing-written — so the Edit footer keeps Undo (revert, stay) and Save
               (commit), and leaving stays where it already is, top right. -->
          <button type="button" class="item-write-footer__cancel" *ngIf="!isEdit" (click)="close()">Cancel</button>

          <!-- Save carries a glyph on Edit, alongside Archive/Restore and Undo: the
               three Edit actions read as one set. Create keeps a plain button — it is
               not one of the three, and a lone icon in that slot would just make the
               two modes look accidentally different. -->
          <!-- Save IS blocked by a conflict (HUBX-8466): Review changes in the band resolves it
               first, member by member against the item as re-read, and Undo discards the edit.
               A Save straight after a 412 used to carry the fresh tokens and overwrite members
               the user never looked at.

               It is also blocked when the Edit would carry nothing. The server treats that as a
               no-op and writes nothing (EditVendorItemExecutor: no UPDATE, no rowversion
               rotation, no fact), so the round-trip only buys a confirmation announcing a
               save that did not happen. `isEdit` guards it because Create has no baseline
               to be unchanged from — without the guard the button would be born disabled.

               And it waits while an error is shown (HUBX-8466), or while the server's 400 on
               this very edit stands: a pristine Create shows none, so its first Save reveals
               what is missing and then waits; Edit shows every refusal once there is a change.

               No tooltip: whatever blocks it is already said on screen - the error banner, or
               the conflict band with Review changes. -->
          <button type="button" class="item-write-footer__submit"
            [disabled]="loader || lookupsError || (isEdit && !hasPendingChanges) || hasConflict || saveBlockedByError"
            (click)="submit()">
            <svg *ngIf="isEdit" width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg">
              <path d="m5 12.5 4.5 4.5L19 7.5" stroke="currentColor" stroke-width="2.5"
                stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span>{{ submitLabel }}</span>
          </button>

          <!-- Read-only (HUBX-8493). The form is already locked — every control disabled and
               Save with nothing to carry — but a locked form does not say why. Shown when
               writableFields is empty, whoever the caller is. The reason it names is the only
               one that empties the list on Edit today: a VendorManager on a vendor B1 does not
               assign to it (HUBX-8492). A rule that empties it for another reason has to change
               this sentence with it. Amber like the notices below, never the error band:
               nothing here failed. -->
          <div class="item-readonly-notice" *ngIf="readOnlyNotice">
            <span>You can view this item but not edit it: its vendor is not assigned to you.</span>
          </div>

          <!-- Write conflict (HUBX-8439). Amber like the other two consequence bars, never
               the pink error band: nothing here is a mistake the user made, and the form is
               still usable.

               It says only that a conflict happened and that nothing was saved, and offers the
               way out: Review changes opens the resolve popup (HUBX-8466), which lists what
               differs and asks which value to keep. The rings say WHERE the other writer
               landed; counting the fields here would say it a second time.

               It cannot name the other writer. `currentState` is the read representation and
               carries no author and no timestamp, so "updated since you opened it" is the most
               this client honestly knows — naming them would mean growing the contract. -->
          <div class="item-conflict-banner" *ngIf="conflictNotice">
            <span>{{ conflictNotice }}</span>
            <button type="button" class="item-conflict-banner__action"
              [disabled]="loader" (click)="reviewConflict()">{{ reviewLabel }}</button>
          </div>

          <!-- Approval intent. Same shape and colour as the archive notice below, and for
               the same reason: it says what SAVE will do, because the user did not ask for
               it in those words — they picked Active, and on an item still in review that
               also approves it. The one irreversible thing this control can do, so it is
               the one that must not happen quietly. -->
          <div class="item-approval-notice" *ngIf="approvalPending">
            <span>{{ approvalNotice }}</span>
          </div>

          <!-- Re-approval intent (HUBX-8367), the mirror of the one above: same shape,
               same colour, and the two can never show together — that one needs a caller
               who may approve, this one needs a caller who may not.

               It says what SAVE will do for the same reason: the user did not ask for it
               in those words either. They raised the cost, or changed MOQ, MXQ, ETA, Special
               Notes or Restrictions on an approved item (HUBX-8515), and the item comes off
               the shelf until HUBX looks at the change. Without this the vendor finds out by
               the row disappearing from Active. -->
          <div class="item-approval-notice" *ngIf="reviewPending">
            <span>{{ reviewNotice }}</span>
          </div>

          <!-- Reactivation intent (HUBX-8476), the third sibling: same shape, same colour.
               Legacy made the vendor pick "Active" once the cost cleared the threshold; under
               v2 Removed is system-only and the reactivation is derived on save, so there is
               nothing to pick — only this to read. Never shows with reviewPending: that one
               needs a cost raise or an approved item, this one a Removed item and a cut below
               the threshold. -->
          <div class="item-approval-notice" *ngIf="reactivationPending">
            <span>{{ reactivationNotice }}</span>
          </div>

          <!-- Manual reactivation (HUBX-8476, ADR-0009 Rule 9), the fourth sibling: an operator
               chose Active on a Removed item whose cost does not bring it back. Save does it
               anyway, so it says so - and names the threshold the cost would have needed. Never
               shows with the one above: it holds only when that one does not. -->
          <div class="item-approval-notice" *ngIf="manualReactivationPending">
            <span>{{ manualReactivationNotice }}</span>
          </div>

          <!-- Pending archive intent (HUBX-8438). Advisory, not a failure: amber like the
               category nudge, never the pink error band. Says what SAVE will do, because
               the button did not do it. -->
          <div class="item-archive-notice" *ngIf="archiveIntentNotice">
            <span>{{ archiveIntentNotice }}</span>
          </div>

          <!-- validation banner (item-details pattern): field rings show WHERE, this
               line says WHAT. Own line at the bottom, below Status and the actions.

               The XID lock (HUBX-8458) adds a Revert beside the message — the one code in
               the table whose way out the form cannot express, because the value to restore
               is the one the user overwrote. The XID is named when the item has one; a blank
               stored XID is legitimate (A-R10 defaults it server-side) and then there is
               nothing to name. -->
          <div class="item-error-banner" *ngIf="bannerError">
            <span>{{ bannerError }}</span>
            <button type="button" class="item-error-banner__revert" *ngIf="xidLockedInB1" (click)="revertXid()">
              {{ storedXid ? 'Revert to ' + storedXid : 'Revert' }}
            </button>
          </div>
        </div>
      </div>
    </form>
  </div>
</div>

<!-- The resolve popup of a write conflict (HUBX-8466), over the modal. -->
<app-conflict-resolve *ngIf="resolveOpen"
  [overModal]="true"
  [partNumber]="target?.vendorItemCode ?? ''"
  [rows]="resolveRows"
  [error]="resolveError"
  (resolved)="onResolved($event)"
  (cancelled)="closeResolve()"
  (retry)="readResolve()">
</app-conflict-resolve>

<app-item-text-modal *ngIf="textModalField"
  [title]="textModalTitle"
  [value]="textModalValue"
  (saved)="onTextModalSaved($event)"
  (closedModal)="onTextModalClosed()">
</app-item-text-modal>
